import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { Link, useNavigate } from 'react-router-dom';
import '../styles.css';

const TIERS = [
  { key: 'member', label: 'Choir Member', price: '₦0 / Free' },
  { key: 'supporter', label: 'Choir Supporter', price: '₦1,000 / month' },
  { key: 'expert', label: 'Choir Expert', price: '₦7,500 / year' },
];

function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [tier, setTier] = useState('member');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  const recaptchaRef = useRef(null);
  const navigate = useNavigate();

  // Load reCAPTCHA script and render the widget automatically
  useEffect(() => {
    const renderRecaptcha = () => {
      if (!window.grecaptcha || !recaptchaRef.current) return;
      if (recaptchaRef.current.childElementCount > 0) return; // Already rendered
      try {
        window.grecaptcha.render(recaptchaRef.current, {
          sitekey: import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LczEuYqAAAAANYh6VG8jSj1Fmt6LKMK7Ee1OcfU',
        });
        setRecaptchaLoaded(true);
      } catch (err) {
        setError('Failed to load reCAPTCHA. Please refresh the page or try again later.');
      }
    };

    if (!window.grecaptcha) {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js';
      script.async = true;
      script.defer = true;
      script.onload = renderRecaptcha;
      document.body.appendChild(script);
    } else {
      renderRecaptcha();
    }
  }, []);

  const verifyRecaptcha = async (token) => {
    try {
      const res = await fetch('/.netlify/functions/verify-recaptcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      return !!data.success;
    } catch {
      return false;
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setLoading(true);

    if (!fullName.trim() || !email.trim() || !password.trim() || !phoneNumber.trim()) {
      setError('Please fill in all fields.');
      setLoading(false);
      return;
    }

    const honeypot = e.target.elements.honeypot.value;
    if (honeypot) {
      setError('Spam detected');
      setLoading(false);
      return;
    }

    if (!recaptchaLoaded || !window.grecaptcha) {
      setError('reCAPTCHA is not loaded yet. Please wait or refresh.');
      setLoading(false);
      return;
    }
    const token = window.grecaptcha.getResponse();
    if (!token) {
      setError('Please complete the reCAPTCHA.');
      setLoading(false);
      return;
    }
    const isRecaptchaValid = await verifyRecaptcha(token);
    if (!isRecaptchaValid) {
      setError('reCAPTCHA verification failed. Try again.');
      setLoading(false);
      window.grecaptcha.reset();
      return;
    }

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({ email, password });
      if (signUpError) throw signUpError;

      if (data.user) {
        // Do not assign supporter/expert tier here!
        const { error: profileError } = await supabase
          .from('profiles')
          .upsert({
            id: data.user.id,
            email: data.user.email,
            full_name: fullName,
            phone_number: phoneNumber,
            subscription_tier: null, // <-- All new users are basic until payment confirmed
            is_admin: false,
          }, { onConflict: 'id' });

        if (profileError) throw profileError;

        setSuccess(true);

        if (tier === 'member') {
          navigate('/profile');
        } else {
          try {
            const res = await fetch('/.netlify/functions/create-paystack-transaction', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: data.user.email,
                user_id: data.user.id,
                tier: tier === 'supporter' ? 'choir_supporter' : 'choir_expert',
                website: window.location.hostname,
              }),
            });
            const result = await res.json();

            if (result.payment_url) {
              window.location.href = result.payment_url;
            } else {
              setError(result.error || "Payment initialization failed. Please try again.");
            }
          } catch (err) {
            setError("Payment initialization error. Please try again.");
          }
        }
      }
    } catch (err) {
      setError(err.message || 'Signup error');
    } finally {
      setLoading(false);
      if (window.grecaptcha) window.grecaptcha.reset();
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card max-w-lg mx-auto">
        <h2 className="auth-title text-green-900 text-2xl font-extrabold mb-2">Sign Up</h2>
        <p className="auth-subtitle text-green-800 mb-5">
          Create your account and join the choir community!
        </p>
        {success ? (
          <div className="text-green-700 my-4 text-center text-lg">
            Signup successful!{' '}
            {tier === 'member' ? "Redirecting..." : "Redirecting to payment..."}
          </div>
        ) : (
          <form onSubmit={handleSignup} className="auth-form mt-2">
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                className="auth-input"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                disabled={loading}
                autoComplete="name"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                className="auth-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                className="auth-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                autoComplete="new-password"
              />
            </div>
            <div className="form-group">
              <label>
                Phone Number <span className="text-red-600">*</span>
              </label>
              <input
                type="tel"
                className="auth-input"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
                disabled={loading}
                autoComplete="tel"
              />
            </div>

            {/* Simple Tier Toggle */}
            <div className="form-group mt-3">
              <label className="mb-1 block">Choose Account Tier</label>
              <div className="flex flex-row justify-between gap-2">
                {TIERS.map((t) => (
                  <button
                    type="button"
                    key={t.key}
                    onClick={() => setTier(t.key)}
                    className={`
                      flex-1 rounded-xl px-3 py-2 border-2 font-semibold text-sm sm:text-base transition-all
                      ${tier === t.key
                        ? t.key === 'expert'
                          ? 'border-yellow-400 bg-yellow-50 ring-2 ring-yellow-300 scale-105 text-yellow-900'
                          : t.key === 'supporter'
                          ? 'border-lime-400 bg-lime-50 ring-2 ring-lime-200 scale-105 text-green-800'
                          : 'border-green-400 bg-green-50 ring-2 ring-green-200 scale-105 text-green-900'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:scale-105'}
                    `}
                    disabled={loading}
                    aria-pressed={tier === t.key}
                  >
                    <div className="flex flex-col items-center">
                      <span className="font-bold">{t.label}</span>
                      <span className="text-xs font-medium mt-0.5">{t.price}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            {/* End Tier Toggle */}

            <input type="text" name="honeypot" className="honeypot" style={{ display: 'none' }} />
            <div ref={recaptchaRef} className="g-recaptcha mt-3"
              data-sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LczEuYqAAAAANYh6VG8jSj1Fmt6LKMK7Ee1OcfU'}
            ></div>
            {!recaptchaLoaded && (
              <p className="text-xs text-green-700 my-2">Loading reCAPTCHA...</p>
            )}
            <button
              type="submit"
              className={`
                auth-button w-full mt-6 text-base font-bold rounded-full py-3
                ${tier === 'expert'
                  ? 'bg-gradient-to-r from-yellow-500 via-yellow-400 to-yellow-300 text-yellow-900'
                  : tier === 'supporter'
                  ? 'bg-gradient-to-r from-green-700 via-lime-400 to-green-400 text-black'
                  : 'bg-gradient-to-r from-green-700 via-green-400 to-lime-300 text-black'}
                shadow hover:scale-105 transition-all
              `}
              disabled={loading || !recaptchaLoaded}
            >
              {loading
                ? 'Signing Up...'
                : tier === 'member'
                ? 'Sign Up as Choir Member'
                : tier === 'supporter'
                ? 'Sign Up as Choir Supporter'
                : 'Sign Up as Choir Expert'}
            </button>
          </form>
        )}
        {error && <div className="text-red-700 mt-3">{error}</div>}
        <div className="mt-6 text-center text-green-700">
          Already have an account?{' '}
          <Link to="/login" className="text-green-800 underline">
            Log in here
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Signup;
